import baseLayers from '../layers';
import { Region } from '../../locations';
import { fitBoundsAsync, newBoundingBoxEvent } from '../bounding-box';
import { filterLayerIfExists, removeApproxLocationCircle, removeLayersForSource, setLayer } from '../layers';
import { newPropertyFilter } from '../request-filters';
import Logger from '../../logging/logger';

class Reset {
    constructor( mapSearch, options = {} ) {
        this.options = Object.assign( {}, options );
        this.mapSearch = mapSearch;
        this.map = undefined;
    }

    init( map ) {
        this.map = map;
        this.mapSearch.on( 'twc:mapsearch:reset', this.trigger.bind( this ) );
    }

    async trigger( event ) {
        this.mapSearch.setLoading( true );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:hide-remove-search-area', { bubbles: true } ) );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );

        this.mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );

        let resetLayers = true;
        let triggerStateChange = true;
        let recalculateBboxAfterAnimation = false;

        const { searchOptions } = event.detail || {};

        if ( searchOptions && 'triggerStateChange' in searchOptions ) {
            triggerStateChange = searchOptions.triggerStateChange;
        }

        if ( searchOptions && 'resetLayers' in searchOptions ) {
            resetLayers = searchOptions.resetLayers;
        }

        if ( searchOptions && 'recalculateBboxAfterAnimation' in searchOptions ) {
            recalculateBboxAfterAnimation = searchOptions.recalculateBboxAfterAnimation;
        }

        Logger.info( '[map] Map Reset Triggered', event );

        this.mapSearch.focusedFeature = null;
        this.mapSearch.currentCursorTarget = null;
        this.mapSearch.currentLocation = null;

        if ( this.mapSearch.propertyPopup ) {
            this.mapSearch.propertyPopup.remove();
        }

        removeApproxLocationCircle( this.map );

        const fitToSpainBounds = ( !( 'focusMapToSearch' in searchOptions ) || ( 'focusMapToSearch' in searchOptions && searchOptions.focusMapToSearch !== false ) );

        if ( fitToSpainBounds ) {
            const bbox = this.mapSearch.locations.spain.bounds.mainland;

            const bboxOptions = {
                padding: {
                    top: 80,
                    right: 50,
                    bottom: 80,
                    left: 50,
                },
            };

            const bboxEvent = newBoundingBoxEvent();

            await fitBoundsAsync( this.mapSearch, {
                bbox: bbox,
                options: bboxOptions,
                event: bboxEvent,
            } );
        }

        if ( this.map.getLayer( 'properties' ) ) {
            this.map.removeLayer( 'properties' );
        }

        if ( this.map.getSource( 'properties' ) ) {
            this.map.removeSource( 'properties' );
        }

        if ( resetLayers ) {
            baseLayers.forEach( ( layer ) => {
                removeLayersForSource( this.map, layer );

                if ( this.map.getSource( layer ) ) {
                    this.map.removeSource( layer );
                }
            } );

            const hasRegionSource = this.map.getSource( Region );
            const hasRegionLayers = this.map.getLayer( `${Region}:fill` ) && this.map.getLayer( `${Region}:outline` );

            if ( !hasRegionSource ) {
                this.map.addSource( Region, {
                    type: 'geojson',
                    data: this.mapSearch.JSON[ Region ],
                    tolerance: 0,
                } );
            }

            if ( !hasRegionLayers ) {
                setLayer( this, {
                    name: Region,
                    source: Region,
                } );
            }

            filterLayerIfExists( this.map, `${Region}:fill`, null, this.mapSearch.debug );
            filterLayerIfExists( this.map, `${Region}:outline`, null, this.mapSearch.debug );

            this.map.setLayoutProperty( `${Region}:fill`, 'visibility', 'visible', this.mapSearch.debug );
            this.map.setLayoutProperty( `${Region}:outline`, 'visibility', 'visible', this.mapSearch.debug );

            if ( this.mapSearch.properties.size > 0 ) {
                this.mapSearch.properties.forEach( ( property ) => property.destroy() );
                this.mapSearch.properties.clear();
            }
        }

        const locationIDInputs = document.querySelectorAll( '[name=locationId][type=hidden]' );
        locationIDInputs.forEach( function( input ) {
            input.value = '';
            input.dataset.isIsland = '';
            input.dataset.isGeocoded = '';
            input.dataset.longitude = '';
            input.dataset.latitude = '';
        } );

        const locationFreetextInputs = document.getElementsByName( 'locationFreeText' );
        locationFreetextInputs.forEach( ( input ) => {
            input.value = '';
        } );

        const propertyParams = newPropertyFilter( {}, this.mapSearch.baseForm );

        this.mapSearch.previousSearchState = propertyParams.toString();

        const options = {
            fetchFeatures: false,
            fetchProperties: true,
            propertySearchURL: this.mapSearch.propertySearchURL,
            propertySearchParams: propertyParams,
            focusMapToSearch: false,
        };

        const meta = {
            recalculateBboxAfterAnimation: recalculateBboxAfterAnimation,
            triggerStateChange: triggerStateChange,
        };

        await this.mapSearch.update( options, meta );

        this.mapSearch.setLoading( false );
    }
}

export default Reset;
